import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Menu, X, ShoppingBag, ShoppingCart, User, TrendingUp, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axiosInstance from "@/api/AxiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import useProducts from "@/hooks/useProducts";
import { useClickOutside } from "@/hooks/useClickOutside";

const Navbar = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);
  const { products } = useProducts();

  useClickOutside(searchRef, () => {
    setIsSearchOpen(false);
  });

  // Default suggestions from existing products (featured / new arrivals)
  const defaultSuggestions = products
    .filter((p: any) => p.is_featured || p.is_new_arrival)
    .filter((p: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.id === p.id) === i)
    .slice(0, 5);

  const popularSearches = ["Hot Wheels", "Resin Art", "Collectibles", "Clothing"];

  const navItems = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Products", path: "/products" },
    { name: "Contact", path: "/contact" },
    { name: "Profile", path: "/profile", desktopOnly: true },
  ];

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        fetchSuggestions(searchTerm);
      } else {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const fetchSuggestions = async (query: string) => {
    try {
      setLoadingSuggestions(true);
      const res = await axiosInstance.get("/api/products/", {
        params: { search: query },
      });
      setSuggestions(res.data.slice(0, 6));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSelect = (product: any) => {
    setSearchTerm("");
    setSuggestions([]);
    // navigate(`/product/${product.id}`);
    navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
      setIsSearchOpen(false);
      setSearchTerm("");
      setSuggestions([]);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-border/50">
      <div className="container mx-auto md:px-6 px-0">
        <div className="flex items-center px-2 justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            {/* <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center group-hover:shadow-medium transition-all duration-300"> */}
            {/* <ShoppingBag className="w-4 h-4 text-primary-foreground" /> */}
            {/* <img
                src="public/fleeby-logo (1).png"
                className="w-10 md:w-12"
                alt="logo"
              /> */}
            {/* </div> */}
            {/* <span className="font-montserrat font-bold text-xl text-primary">
              FleeBy
            </span> */}
            <img src="/text_fleeby.png" className="w-16 md:w-20" alt="logo" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => {
              if (item.desktopOnly)
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`relative font-medium transition-all duration-300 ${
                      isActive(item.path)
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {item.name}
                    {isActive(item.path) && (
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
                    )}
                  </Link>
                );
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`relative font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.name}
                  {isActive(item.path) && (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Search, User & Mobile Menu */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="hover-lift"
              aria-label={isSearchOpen ? "Close search" : "Open search"}
            >
              <Search className="w-5 h-5 " strokeWidth={3} />
            </Button>

            {/* Desktop User Icon */}
            <Button
              variant="ghost"
              size="icon"
              className="hidden sm:flex hover-lift"
              onClick={() => navigate("/profile")}
              aria-label="Go to profile"
            >
              <User className="w-5 h-5" strokeWidth={3} />
            </Button>

            {/* Mobile User Icon */}
            <Button
              variant="ghost"
              size="icon"
              className="sm:hidden hover-lift"
              onClick={() => navigate("/profile")}
              aria-label="Go to profile"
            >
              <User className="w-5 h-5 " strokeWidth={3} />
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close Menu" : "Open Menu"}
            >
              {isMenuOpen ? (
                <X className="w-5 h-5 " strokeWidth={3} />
              ) : (
                <Menu className="w-5 h-5 " strokeWidth={3} />
              )}
            </Button>
          </div>
        </div>

        {/* Search Bar + Dropdown */}
        {isSearchOpen && (
          <div className="py-4 px-2 border-t border-border/50 animate-fade-in" ref={searchRef}>
            <div className="max-w-md mx-auto relative">
              <form action="" onSubmit={handleSubmit}>
                <Input
                  placeholder="Search products..."
                  className="w-full"
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchTerm.trim()) {
                      navigate(
                        `/products?search=${encodeURIComponent(
                          searchTerm.trim()
                        )}`
                      );
                      setIsSearchOpen(false);
                      setSearchTerm("");
                    }
                  }}
                />
              </form>

              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-[9999] bg-white border border-gray-200 rounded-2xl w-full mt-2 shadow-2xl max-h-[360px] overflow-y-auto"
                >
                  {/* Default suggestions — before typing */}
                  {searchTerm.trim().length <= 1 && !loadingSuggestions && (
                    <div className="p-3">
                      {/* Popular search chips */}
                      <div className="mb-3">
                        <div className="flex items-center gap-1.5 mb-2 px-1">
                          <TrendingUp className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Popular Searches</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {popularSearches.map((term) => (
                            <button
                              key={term}
                              onClick={() => {
                                setSearchTerm(term);
                                navigate(`/products?search=${encodeURIComponent(term)}`);
                                setIsSearchOpen(false);
                              }}
                              className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-medium rounded-full border border-gray-200 transition-colors"
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Trending product suggestions */}
                      {defaultSuggestions.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-2 px-1">
                            <Sparkles className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Trending Products</span>
                          </div>
                          <ul>
                            {defaultSuggestions.map((product: any) => (
                              <li
                                key={product.id}
                                onClick={() => {
                                  navigate(`/product/${product.slug}`);
                                  setIsSearchOpen(false);
                                }}
                                className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                {product.images?.[0]?.image ? (
                                  <img
                                    src={product.images[0].image}
                                    alt={product.name}
                                    className="w-10 h-10 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <Search className="w-4 h-4 text-gray-300" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">{product.name}</p>
                                  <p className="text-xs text-gray-400">
                                    ₹{product.price}
                                    {product.category?.name && (
                                      <span> · {product.category.name}</span>
                                    )}
                                  </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Typed suggestions — API results */}
                  {searchTerm.trim().length > 1 && (
                    <div className="p-2">
                      {loadingSuggestions ? (
                        <div className="flex items-center gap-2 px-3 py-4 justify-center">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                          <span className="text-sm text-gray-400">Searching...</span>
                        </div>
                      ) : suggestions.length > 0 ? (
                        <ul>
                          {suggestions.map((product) => (
                            <li
                              key={product.id}
                              onClick={() => handleSelect(product)}
                              className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              {product.images?.[0]?.image ? (
                                <img
                                  src={product.images[0].image}
                                  alt={product.name}
                                  className="w-10 h-10 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                  <Search className="w-4 h-4 text-gray-300" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{product.name}</p>
                                <p className="text-xs text-gray-400">
                                  ₹{product.price}
                                  {product.category?.name && (
                                    <span> · {product.category.name}</span>
                                  )}
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                            </li>
                          ))}
                          {/* View all results */}
                          <li
                            onClick={() => {
                              navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
                              setIsSearchOpen(false);
                              setSearchTerm("");
                            }}
                            className="flex items-center justify-center gap-2 px-3 py-2.5 mt-1 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                          >
                            <Search className="w-3.5 h-3.5 text-gray-500" />
                            <span className="text-xs font-medium text-gray-600">View all results for "{searchTerm}"</span>
                          </li>
                        </ul>
                      ) : (
                        <div className="text-center py-6">
                          <Search className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">No products found</p>
                          <p className="text-xs text-gray-300 mt-0.5">Try a different search term</p>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4  border-border/60 shadow-md border-b  animate-fade-in">
            <div className="space-y-2">
              {navItems
                .filter((item) => !item.desktopOnly)
                .map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`block px-4 py-2 rounded-lg transition-all duration-300 ${
                      isActive(item.path)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
